/**
 * Product Configurator API Service
 *
 * Fetches product variants and add-ons from ERPNext
 */

import type { ProductVariant, AddOn } from '@/features/ProductConfigurator/types';

const ERPNEXT_URL = import.meta.env.VITE_ERPNEXT_URL || '';

interface ERPNextItem {
  name: string;
  item_code: string;
  item_name: string;
  item_group: string;
  description?: string;
  variant_of?: string;
  attributes?: Array<{
    attribute: string;
    attribute_value: string;
  }>;
}

interface ERPNextItemPrice {
  item_code: string;
  price_list_rate: number;
  currency: string;
}

interface ERPNextBin {
  item_code: string;
  actual_qty: number;
}

/**
 * Fetch product variants (color variants)
 */
export async function fetchProductVariants(templateItemCode: string): Promise<ProductVariant[]> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    // Fetch variant items
    const itemsResponse = await fetch(
      `${baseUrl}/api/resource/Item?` +
        new URLSearchParams({
          fields: JSON.stringify(['name', 'item_code', 'item_name', 'attributes']),
          filters: JSON.stringify([['variant_of', '=', templateItemCode]]),
          limit_page_length: '999',
        }),
      { credentials: 'include' }
    );

    if (!itemsResponse.ok) {
      throw new Error(`Failed to fetch variants: ${itemsResponse.status}`);
    }

    const itemsData = await itemsResponse.json();
    const items: ERPNextItem[] = itemsData.data || [];

    // Fetch prices and stock for each variant
    const variants = await Promise.all(
      items.map(async (item) => {
        const [price, stock] = await Promise.all([
          fetchItemPrice(item.item_code),
          fetchItemStock(item.item_code),
        ]);

        // Extract color from item name (e.g., "PadBox GS [Essential] - Orange")
        const colorMatch = item.item_name.match(/- (.+)$/);
        const color = colorMatch ? colorMatch[1] : 'Unknown';

        return {
          id: item.name,
          sku: item.item_code,
          color,
          price,
          stock,
        };
      })
    );

    return variants;
  } catch (error) {
    console.error('Error fetching variants:', error);
    throw error;
  }
}

/**
 * Fetch add-on items
 */
export async function fetchAddOns(): Promise<AddOn[]> {
  try {
    const baseUrl = ERPNEXT_URL || '';

    // Fetch items in Accessories group
    const itemsResponse = await fetch(
      `${baseUrl}/api/resource/Item?` +
        new URLSearchParams({
          fields: JSON.stringify(['name', 'item_code', 'item_name', 'description']),
          filters: JSON.stringify([['item_group', '=', 'Accessories']]),
          limit_page_length: '999',
        }),
      { credentials: 'include' }
    );

    if (!itemsResponse.ok) {
      throw new Error(`Failed to fetch add-ons: ${itemsResponse.status}`);
    }

    const itemsData = await itemsResponse.json();
    const items: ERPNextItem[] = itemsData.data || [];

    // Fetch prices and stock
    const addons = await Promise.all(
      items.map(async (item) => {
        const [price, stock] = await Promise.all([
          fetchItemPrice(item.item_code),
          fetchItemStock(item.item_code),
        ]);

        // Determine category from item code
        let category: 'dpad' | 'adapter' | 'other' = 'other';
        if (item.item_code.includes('DPAD')) {
          category = 'dpad';
        } else if (item.item_code.includes('ADAPTER')) {
          category = 'adapter';
        }

        return {
          id: item.name,
          sku: item.item_code,
          name: item.item_name,
          description: item.description,
          price,
          stock,
          category,
        };
      })
    );

    return addons;
  } catch (error) {
    console.error('Error fetching add-ons:', error);
    throw error;
  }
}

/**
 * Fetch price for a specific item
 */
async function fetchItemPrice(itemCode: string): Promise<number> {
  try {
    const baseUrl = ERPNEXT_URL || '';
    const response = await fetch(
      `${baseUrl}/api/resource/Item Price?` +
        new URLSearchParams({
          fields: JSON.stringify(['price_list_rate', 'currency']),
          filters: JSON.stringify([
            ['item_code', '=', itemCode],
            ['price_list', '=', 'Standard Selling'],
          ]),
        }),
      { credentials: 'include' }
    );

    if (!response.ok) {
      return 0;
    }

    const data = await response.json();
    const prices: ERPNextItemPrice[] = data.data || [];

    return prices.length > 0 ? prices[0].price_list_rate : 0;
  } catch (error) {
    console.error(`Error fetching price for ${itemCode}:`, error);
    return 0;
  }
}

/**
 * Fetch stock level for a specific item
 */
async function fetchItemStock(itemCode: string): Promise<number> {
  try {
    const baseUrl = ERPNEXT_URL || '';
    const response = await fetch(
      `${baseUrl}/api/resource/Bin?` +
        new URLSearchParams({
          fields: JSON.stringify(['actual_qty']),
          filters: JSON.stringify([['item_code', '=', itemCode]]),
        }),
      { credentials: 'include' }
    );

    if (!response.ok) {
      return 0;
    }

    const data = await response.json();
    const bins: ERPNextBin[] = data.data || [];

    // Sum stock across all warehouses
    const total = bins.reduce((sum, bin) => sum + (bin.actual_qty || 0), 0);

    return total;
  } catch (error) {
    console.error(`Error fetching stock for ${itemCode}:`, error);
    return 0;
  }
}

/**
 * Fetch complete product configuration data
 */
export async function fetchProductConfiguration(templateItemCode: string) {
  const [variants, addons] = await Promise.all([
    fetchProductVariants(templateItemCode),
    fetchAddOns(),
  ]);

  return { variants, addons };
}
